import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText, Loader2, Plus, Printer, Receipt, X } from "lucide-react";
import { useState } from "react";
import { api, reciboPdfUrl } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";
import { documentoValido } from "../lib/documento";

/** Interpreta um valor no padrão Brasil: ponto = separador de milhar,
 *  vírgula = casas decimais. Aceita "R$", espaços e formatos como
 *  "1.500,00", "1500,5", "150" ou "1.234". Devolve NaN se não der. */
function parseValorBR(s: string): number {
  const limpo = (s || "").replace(/[^\d.,]/g, "");   // tira R$, espaços, letras
  if (!limpo) return NaN;
  const norm = limpo.replace(/\./g, "").replace(",", ".");  // milhar fora, vírgula → ponto
  const n = Number(norm);
  return Number.isFinite(n) ? n : NaN;
}

interface Props {
  condominioId: number;
  onVoltar: () => void;
}

interface ReciboItem {
  id: number;
  rotulo: string;
  data_emissao: string | null;
  recebedor_nome: string;
  recebedor_documento: string | null;
  valor: number | null;
  valor_extenso: string | null;
  descricao: string;
  forma_pagamento: string | null;
  competencia_ref: string | null;
  status: string;
  criado_por_nome: string | null;
}

interface Profissional {
  id: number;
  nome: string;
  telefone: string;
}

const FORM_VAZIO = {
  profissional_id: "" as string,
  recebedor_nome: "",
  recebedor_documento: "",
  recebedor_telefone: "",
  valor: "",
  data_emissao: new Date().toISOString().slice(0, 10),
  forma_pagamento: "",
  competencia_ref: "",
  descricao: "",
  observacao: "",
};

function brl(v: number | null): string {
  if (v == null) return "—";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

export function RecibosPage({ condominioId, onVoltar }: Props) {
  const qc = useQueryClient();
  const { podeEscrever } = usePermissoes();
  const podeEmitir = podeEscrever(condominioId, "recibos");

  const [form, setForm] = useState({ ...FORM_VAZIO });
  const [aberto, setAberto] = useState(false);

  const { data, isLoading } = useQuery<{ itens: ReciboItem[] }>({
    queryKey: ["recibos", condominioId],
    queryFn: () => api.get<{ itens: ReciboItem[] }>(`/condominios/${condominioId}/recibos`),
  });

  const { data: proximo } = useQuery<{ rotulo: string }>({
    queryKey: ["recibos", condominioId, "proximo"],
    queryFn: () => api.get<{ rotulo: string }>(`/condominios/${condominioId}/recibos/proximo`),
    enabled: aberto,
  });

  const { data: profs } = useQuery<{ itens: Profissional[] }>({
    queryKey: ["profissionais", condominioId, "min"],
    queryFn: () => api.get<{ itens: Profissional[] }>(`/condominios/${condominioId}/profissionais`),
    enabled: aberto,
  });

  const emitir = useMutation({
    mutationFn: () =>
      api.post<ReciboItem>(`/condominios/${condominioId}/recibos`, {
        recebedor_nome: form.recebedor_nome.trim(),
        recebedor_documento: form.recebedor_documento.trim() || null,
        recebedor_telefone: form.recebedor_telefone.trim() || null,
        profissional_id: form.profissional_id ? Number(form.profissional_id) : null,
        valor: parseValorBR(form.valor),
        descricao: form.descricao.trim(),
        forma_pagamento: form.forma_pagamento.trim() || null,
        competencia_ref: form.competencia_ref || null,
        observacao: form.observacao.trim() || null,
        data_emissao: form.data_emissao || null,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["recibos", condominioId] });
      setForm({ ...FORM_VAZIO });
      setAberto(false);
      window.open(reciboPdfUrl(r.id), "_blank");
    },
  });

  const cancelar = useMutation({
    mutationFn: (id: number) => api.post(`/recibos/${id}/cancelar`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recibos", condominioId] }),
  });

  function escolherProfissional(pid: string) {
    const p = profs?.itens.find((x) => String(x.id) === pid);
    setForm((f) => ({
      ...f,
      profissional_id: pid,
      recebedor_nome: p ? p.nome : f.recebedor_nome,
      recebedor_telefone: p ? p.telefone || f.recebedor_telefone : f.recebedor_telefone,
    }));
  }

  const docOk = documentoValido(form.recebedor_documento);
  const podeSalvar =
    form.recebedor_nome.trim() && form.descricao.trim() &&
    parseValorBR(form.valor) > 0 && docOk;
  const inp = "w-full text-sm rounded-md border border-slate-300 px-2.5 py-1.5";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <button type="button" onClick={onVoltar}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-2">
        <ArrowLeft size={16} /> Voltar
      </button>

      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
            <Receipt size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Recibos</h2>
            <p className="text-xs text-slate-500">
              Recibos digitais com numeração própria — substituem o recibo preenchido à mão.
            </p>
          </div>
        </div>
        {podeEmitir && !aberto && (
          <button type="button" onClick={() => setAberto(true)}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-brand-700 text-white hover:brightness-110">
            <Plus size={16} /> Novo recibo
          </button>
        )}
      </div>

      {/* Formulário de emissão */}
      {podeEmitir && aberto && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">
              Emitir recibo {proximo?.rotulo && <span className="text-brand-700">— {proximo.rotulo}</span>}
            </h3>
            <button type="button" onClick={() => { setAberto(false); setForm({ ...FORM_VAZIO }); }}
              className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-500">Puxar de um profissional cadastrado (opcional)</label>
              <select className={inp} value={form.profissional_id}
                onChange={(e) => escolherProfissional(e.target.value)}>
                <option value="">— selecionar —</option>
                {profs?.itens.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs text-slate-500">Nome de quem recebeu *</label>
              <input className={inp} value={form.recebedor_nome}
                onChange={(e) => setForm({ ...form, recebedor_nome: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-500">CPF/CNPJ</label>
              <input
                className={`${inp} ${!docOk ? "border-rose-400 focus:border-rose-400" : ""}`}
                value={form.recebedor_documento}
                onChange={(e) => setForm({ ...form, recebedor_documento: e.target.value })} />
              {!docOk && (
                <p className="text-[11px] text-rose-600 mt-0.5">CPF/CNPJ inválido (o dígito verificador não bate).</p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-500">Telefone</label>
              <input className={inp} value={form.recebedor_telefone}
                onChange={(e) => setForm({ ...form, recebedor_telefone: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Valor (R$) *</label>
              <input className={inp} inputMode="decimal" placeholder="0,00" value={form.valor}
                onChange={(e) => setForm({ ...form, valor: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Data</label>
              <input type="date" className={inp} value={form.data_emissao}
                onChange={(e) => setForm({ ...form, data_emissao: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Forma de pagamento</label>
              <input className={inp} placeholder="PIX, dinheiro, transferência…" value={form.forma_pagamento}
                onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-500">Descrição do serviço executado *</label>
              <textarea className={`${inp} min-h-[90px]`} value={form.descricao}
                placeholder="Descreva o serviço realizado com detalhes…"
                onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Mês de referência (balancete)</label>
              <input type="month" className={inp} value={form.competencia_ref}
                onChange={(e) => setForm({ ...form, competencia_ref: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Observação</label>
              <input className={inp} value={form.observacao}
                onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button type="button" disabled={!podeSalvar || emitir.isPending}
              onClick={() => emitir.mutate()}
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-brand-700 text-white hover:brightness-110 disabled:opacity-50">
              {emitir.isPending ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
              Emitir e abrir PDF
            </button>
            {emitir.isError && <span className="text-xs text-rose-600">Não foi possível emitir. Confira os campos.</span>}
          </div>
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
          <Loader2 size={18} className="animate-spin" /> Carregando…
        </div>
      ) : !data || data.itens.length === 0 ? (
        <div className="text-center text-slate-500 py-12 border border-dashed border-slate-200 rounded-lg">
          Nenhum recibo emitido ainda.
        </div>
      ) : (
        <div className="space-y-2">
          {data.itens.map((r) => (
            <div key={r.id}
              className={`border rounded-lg p-3 bg-white flex flex-wrap items-center gap-3 ${r.status === "cancelado" ? "border-rose-200 opacity-70" : "border-slate-200"}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-800">{r.rotulo}</span>
                  <span className="text-xs text-slate-400">{fmtData(r.data_emissao)}</span>
                  {r.status === "cancelado" && (
                    <span className="text-[11px] font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5">
                      cancelado
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-600">{r.recebedor_nome}</div>
                <div className="text-xs text-slate-400 line-clamp-1">{r.descricao}</div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-slate-800">{brl(r.valor)}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <a href={reciboPdfUrl(r.id)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
                  <Printer size={14} /> PDF
                </a>
                {podeEmitir && r.status !== "cancelado" && (
                  <button type="button"
                    onClick={() => { if (confirm(`Cancelar o recibo ${r.rotulo}?`)) cancelar.mutate(r.id); }}
                    className="text-sm px-2.5 py-1.5 rounded-md border border-slate-200 text-rose-600 hover:bg-rose-50">
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
